import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { CurrentUser } from '../../../common/models/user';
import { StorageService } from '../../../common/services/storage.service';
import { SearchService } from '../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { ApplicationService } from '../../../common/services/application.service';
import { ApplicationFormType, ApplicationSummary, GetApplicationsFilter } from '../../../common/models/application';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FilterService } from '../../../common/components/filter/services/filter.service';
import { ApplicationsFilterBuilder } from '../../../common/components/filter/builders/applications-filter-builder';
import { Subject, takeUntil } from 'rxjs';
import { ApplicationStatus } from '../../../common/enums/application-status';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';

@Component({
    selector: 'app-applications',
    templateUrl: './applications.component.html',
    styleUrl: './applications.component.scss',
    imports: [MatCardModule, MatMenuModule, MatButtonModule, MatPaginatorModule, MatTooltipModule, MatIconModule, MatDialogModule, MatSnackBarModule, MatCheckboxModule, CommonModule]
})
export class ApplicationsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    applications: ApplicationSummary[] = [];
    filteredApplications: ApplicationSummary[] = [];
    public ApplicationStatus = ApplicationStatus;
    public ApplicationFormType = ApplicationFormType;
    user: CurrentUser | null = null;
    isToggled = false;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };

    constructor(
      private router: Router,
      private filterBuilder: ApplicationsFilterBuilder,
      private applicationService: ApplicationService,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService,
      private snackBar: MatSnackBar,
      private dialog: MatDialog
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });   
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
        this.filterService.setFiltersInScope(filters);
        this.fetchApplications();
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters();
      });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();
    
      const config = this.filterBuilder
        .reset()
        .years(startYear)
        .status()
        .build();

      this.filterService.setFilterConfig(config);
      this.filterService.restoreFiltersInScope({ year: currentYear });

    }

    fetchApplications(): void {
        const savedFilterValues = this.filterService.getFiltersInScope();

        const filter = new GetApplicationsFilter(
          this.user?.municipalityId, 
          undefined, 
          savedFilterValues?.status, 
          savedFilterValues?.year, 
          savedFilterValues?.grantName
        );

        this.applicationService.getApplications(filter).pipe(takeUntil(this.destroy$)).subscribe({
          next: (data) => {
            this.applications = data.map(a => ApplicationSummary.fromJson(a));
            this.filteredApplications = [...this.applications];
          },
          error: (error) => {
            console.error('Greška prilikom dohvata prijava', error);
          }
        });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredApplications = this.applications.filter((application: ApplicationSummary) => {
          return (
            application.grantName.toLowerCase().includes(searchTerm) ||
            application.organizationName.toLowerCase().includes(searchTerm) ||
            application.programProjectName.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredApplications = [...this.applications];
      }
    }

    confirmReturn(application: ApplicationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite vraćanje',
          message: `Jeste li sigurni da želite vratiti prijavu "${application.programProjectName}" na doradu?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.returnApplication(application);
        }
      });
    }

    private returnApplication(application: ApplicationSummary): void {
      this.applicationService.return(application.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Prijava je vraćena u obradu', 'Zatvori', this.snackbarConfig);
          this.fetchApplications();
        },
        error: (error) => {
          console.error('Greška prilikom vraćanja prijave u obradu', error);
        }
      });
    }

    confirmApprove(application: ApplicationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite odobravanje',
          message: `Jeste li sigurni da želite odobriti prijavu "${application.programProjectName}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.approveApplication(application);
        }
      });
    }

    private approveApplication(application: ApplicationSummary): void {
      this.applicationService.approve(application.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Prijava je odobrena', 'Zatvori', this.snackbarConfig);
          this.fetchApplications();
        },
        error: (error) => {
          console.error('Greška prilikom odobravanja prijave', error);
        }
      });
    }

    confirmReject(application: ApplicationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite odbijanje',
          message: `Jeste li sigurni da želite odbiti prijavu "${application.programProjectName}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.rejectApplication(application);
        }
      });
    }

    private rejectApplication(application: ApplicationSummary): void {
      this.applicationService.reject(application.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Prijava je odbijena', 'Zatvori', this.snackbarConfig);
          this.fetchApplications();
        },
        error: (error) => {
          console.error('Greška prilikom odbacivanja prijave', error);
        }
      });
    }

    downloadForms(application: ApplicationSummary, formTypes: ApplicationFormType[]): void {
      this.applicationService.downloadForms(application.id, formTypes).pipe(takeUntil(this.destroy$)).subscribe({
          next: (blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'prijavna-dokumentacija.zip';
              a.click();
              window.URL.revokeObjectURL(url);
          },
          error: (err) => {
              console.error('Error downloading application forms', err);
          }
      });
    }

    goToCreateProject(application: ApplicationSummary): void {
      this.router.navigate(['municipality', 'projects', application.id.toString(), 'create'], 
      { state: { programProjectName: application.programProjectName }});
    }

    goToViewApplication(application: ApplicationSummary): void {
      this.router.navigate(['municipality', 'applications', application.id.toString(), 'view']);
    }

    goToComplyApplication(application: ApplicationSummary): void {
      this.router.navigate(['municipality', 'applications', application.id.toString(), 'comply']);
    }

    goToReviewApplication(application: ApplicationSummary): void {
        this.router.navigate(['municipality', 'applications', application.id.toString(), 'review']);
    }

    goToApplicationReviews(application: ApplicationSummary): void {
      this.router.navigate(['municipality', 'applications', application.id.toString(), 'reviews' ]);
    }
  }
